import React, { useState, useEffect } from 'react'
import { useSlerp } from '@slerp/client'
import { useQuery, useMutation } from '@apollo/client'
import { Select, Form, Button, Modal, message } from 'antd'
import { requiredRule } from './Rules'
import { TextFieldNumber } from './../Widgets'
import { LoyaltyCustomerListItem, LoyaltyCardType, StoreType } from './types'
import {
  QUERY_LOYALTY_CARDS,
  STAMP_CUSTOMER_LOYALTY_CARD,
  GENERATE_STAMP_QR_TOKEN
} from './LoyaltyQueries'
import { filterStoresForQR } from './utils'
import { LoyaltyCustomer } from './LoyaltyOverview'
import { useMerchant } from 'hooks/useGlobalContext'

export type Customer = Partial<LoyaltyCustomerListItem> &
  Partial<LoyaltyCustomer>

interface StampCustomerModalProps {
  customer: Customer | null
  visible: boolean
  onClose: () => void
  onSuccess?: (stampsAdded: number) => void
}

const { useForm } = Form

const StampCustomerModal = (props: StampCustomerModalProps) => {
  const { visible, onClose, customer, onSuccess } = props
  const { merchantLocations, merchantData } = useMerchant()
  const [form] = useForm()
  const { user } = useSlerp()

  const { data: loyaltyCards } = useQuery(QUERY_LOYALTY_CARDS, {
    variables: {
      merchant_id: user?.merchant?.id
    },
    fetchPolicy: 'no-cache'
  })

  const [stampCard, { loading: stampLoading }] = useMutation(
    STAMP_CUSTOMER_LOYALTY_CARD,
    {
      fetchPolicy: 'no-cache'
    }
  )

  const [generateStampToken] = useMutation(GENERATE_STAMP_QR_TOKEN, {
    fetchPolicy: 'no-cache'
  })

  const [selectedCard, setSelectedCard] = useState<LoyaltyCardType | null>(null)
  const [validStores, setValidStores] = useState<StoreType[]>([])

  useEffect(() => {
    if (!selectedCard) return
    setValidStores(
      filterStoresForQR(
        merchantLocations || [],
        selectedCard.all_stores_enabled,
        selectedCard.store_ids?.length ? selectedCard.store_ids : undefined
      )
    )
  }, [selectedCard, merchantLocations])

  const stampCustomerCard = async (values: {
    code: string
    store_id: string
    stamps_per_scan: number
  }) => {
    let code = values.code

    if (merchantData?.loyalty_scan_method === 'partner' && selectedCard) {
      try {
        const tokenResult = await generateStampToken({
          variables: { loyaltyCardId: selectedCard.id, secondsExpiration: null }
        })
        code = tokenResult.data.generateStampToken.code
      } catch (error) {
        console.error('Generate stamp token error: ', error)
        message.error(`Unable to generate stamp token due to ${error}.`, 3)
        return
      }
    }

    const stampsAdded = Number(values.stamps_per_scan)
    const params = {
      ...values,
      code,
      stamps_per_scan: stampsAdded,
      customer_id: customer?.id
    }

    message.loading('Stamping Customer Card. Please wait.', 1)
    stampCard({ variables: params })
      .then(() => {
        message.destroy()
        message.success('Customer loyalty card stamp success!', 1)
        onSuccess?.(stampsAdded)
        onClose()
      })
      .catch((error) => {
        console.error('Stamp Customer Card Error: ', error)
        message.destroy()
        message.error(
          `Unable to stamp customer loyalty card due to ${error}.`,
          3
        )
      })
  }

  const modalFooter = [
    <Button key='discard' type='ghost' onClick={onClose}>
      Discard
    </Button>,
    <Button
      key='submit'
      type='primary'
      htmlType='submit'
      onClick={() => {
        form.submit()
      }}
      loading={stampLoading}
    >
      Add Stamps
    </Button>
  ]

  const modalTitle = (
    <div className='qr-modal-header'>
      <h2 className='title'>Add Stamps</h2>
    </div>
  )

  return (
    //@ts-ignore
    <Modal
      closable={false}
      onCancel={onClose}
      open={visible}
      destroyOnClose={true}
      forceRender={true}
      className='loyalty-qr-modal'
      title={modalTitle}
      footer={modalFooter}
    >
      <Form
        className='_pl-4'
        form={form}
        onFinish={stampCustomerCard}
        layout='vertical'
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 20 }}
      >
        <Form.Item label='Customer:'>
          <strong>{`${customer?.firstName || customer?.first_name} ${
            customer?.lastName || customer?.last_name
          }`}</strong>
        </Form.Item>
        <Form.Item name='code' label='Loyalty Card:' rules={requiredRule}>
          <Select
            className='_mt-10'
            placeholder='Select card'
            onChange={(value) => {
              const selected = loyaltyCards?.loyalty_cards?.find(
                (card: LoyaltyCardType) => card.code === value
              )
              setSelectedCard(selected)
            }}
          >
            {loyaltyCards?.loyalty_cards
              ?.filter(
                (card: LoyaltyCardType) => !card.archived_at && card.published
              )
              .map((card: LoyaltyCardType) => (
                <Select.Option key={card?.id} value={card?.code}>
                  {card?.name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
        {selectedCard && (
          <Form.Item name='store_id' label='Store:' rules={requiredRule}>
            <Select className='_mt-10' placeholder='Select store'>
              {validStores?.map((store: StoreType) => (
                <Select.Option key={store?.id} value={store?.id}>
                  {store?.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}
        <Form.Item
          name='stamps_per_scan'
          label='Number of stamps/points:'
          rules={[
            ...requiredRule,
            {
              validator: (_, val) => {
                if (!val) return Promise.resolve()

                if (!Number.isInteger(val)) {
                  return Promise.reject('Decimal is not allowed')
                }
                return Promise.resolve()
              }
            }
          ]}
        >
          <TextFieldNumber
            inputType='number'
            min={1}
            step={1}
            style={{ maxWidth: '160px' }}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default StampCustomerModal
